import {
  base64URLStringToBuffer,
  startAuthentication,
  startRegistration,
  WebAuthnError,
} from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { fromB64, toB64, unwrapPrivateKeyWithPrf, wrapPrivateKeyWithPrf } from './chatCrypto';
import type { KeyWrap } from './chatCrypto';
import {
  assertPasskey,
  getPasskeyAssertionOptions,
  getPasskeyRegistrationOptions,
  listPasskeys as listPasskeysRoute,
  registerPasskey,
  removePasskey as removePasskeyRoute,
} from './chatService';
import type { PasskeyInfo, PasskeyRegistrationPurpose } from './chatService';

/**
 * WebAuthn ceremonies for the chat identity key (passkeys with the PRF
 * extension). The PRF output is the only secret this module handles: it is
 * turned into the wrap key in chatCrypto, zero-filled afterwards, and stripped
 * from every response before that response is sent to the backend.
 */

export type PasskeyFailure =
  | 'prf-unavailable'
  | 'no-passkey'
  | 'prf-unwrap-failed'
  | 'not-verified'
  | 'already-registered'
  | 'cancelled';

export class PasskeyError extends Error {
  readonly reason: PasskeyFailure;

  constructor(reason: PasskeyFailure, message?: string) {
    super(message ?? reason);
    this.name = 'PasskeyError';
    this.reason = reason;
  }
}

export const PASSKEY_UNSUPPORTED_MESSAGE =
  'This browser cannot make a passkey for chat. Use Chrome, Edge, Safari 18 or later, or Firefox 135 or later, or scan the QR code with your phone.';
export const PASSKEY_CANNOT_UNLOCK_MESSAGE =
  'This passkey cannot unlock chat on this device. Use your recovery key.';
export const NO_PASSKEY_MESSAGE = 'No passkey on this device can unlock chat. Use your recovery key.';
/** A 401 from POST /passkeys/assert (bad signature, or a counter that did not advance). */
export const PASSKEY_NOT_VERIFIED_MESSAGE = 'Could not verify your passkey.';
export const PASSKEY_ALREADY_ON_DEVICE_MESSAGE =
  'This device already has a passkey for chat. To add another, use a security key or scan the QR code with your phone.';

/** A settings write may follow an assertion this recent without a new touch. */
export const RECENT_ASSERTION_MS = 5 * 60 * 1000;

let lastAssertionAt: number | null = null;
let assertionFailed = false;

export function hasRecentAssertion(now: number = Date.now()): boolean {
  return lastAssertionAt != null && now - lastAssertionAt <= RECENT_ASSERTION_MS;
}

/** Whether an assertion was cancelled or failed in this session (the settings gate then signs in afresh). */
export function assertionFailedThisSession(): boolean {
  return assertionFailed;
}

export function resetPasskeySessionState(): void {
  lastAssertionAt = null;
  assertionFailed = false;
}

// ---------------------------------------------------------------------------
// PRF extension plumbing (SimpleWebAuthn passes `extensions` through untouched)
// ---------------------------------------------------------------------------

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

/** Replace the base64url `prf.eval.first` the backend serialized with the bytes the browser expects. */
function withPrfInput<T extends { extensions?: unknown }>(options: T, fallbackSalt: Uint8Array): T {
  const extensions = { ...((options.extensions as Record<string, unknown> | undefined) ?? {}) };
  const served = (extensions.prf as { eval?: { first?: unknown } } | undefined)?.eval?.first;
  const first =
    typeof served === 'string' ? base64URLStringToBuffer(served) : toArrayBuffer(fallbackSalt);
  extensions.prf = { eval: { first } };
  return { ...options, extensions: extensions as T['extensions'] };
}

function readPrfOutput(results: unknown): Uint8Array | null {
  const first = (results as { prf?: { results?: { first?: unknown } } } | undefined)?.prf?.results?.first;
  if (first instanceof ArrayBuffer) return new Uint8Array(first.slice(0));
  if (ArrayBuffer.isView(first)) {
    return new Uint8Array(first.buffer.slice(first.byteOffset, first.byteOffset + first.byteLength));
  }
  return null;
}

/** The PRF output never leaves the browser: keep only `enabled` in what goes to the server. */
function stripPrfResults<T extends { clientExtensionResults: unknown }>(json: T): T {
  const results = { ...((json.clientExtensionResults as Record<string, unknown> | undefined) ?? {}) };
  const prf = results.prf as { enabled?: boolean } | undefined;
  if (prf) results.prf = { enabled: prf.enabled === true };
  return { ...json, clientExtensionResults: results as T['clientExtensionResults'] };
}

function isWebAuthnErrorNamed(err: unknown, name: string): boolean {
  const candidate = err as { name?: string; cause?: { name?: string }; code?: string } | undefined;
  return candidate?.name === name || candidate?.cause?.name === name;
}

function classifyRegistrationError(err: unknown): Error {
  if (
    (err instanceof WebAuthnError && err.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') ||
    isWebAuthnErrorNamed(err, 'InvalidStateError')
  ) {
    return new PasskeyError('already-registered', PASSKEY_ALREADY_ON_DEVICE_MESSAGE);
  }
  if (
    (err instanceof WebAuthnError && err.code === 'ERROR_CEREMONY_ABORTED') ||
    isWebAuthnErrorNamed(err, 'NotAllowedError') ||
    isWebAuthnErrorNamed(err, 'AbortError')
  ) {
    return new PasskeyError('cancelled', 'The passkey prompt was cancelled.');
  }
  return err instanceof Error ? err : new Error(String(err));
}

/**
 * The one-assertion fallback for authenticators that return no PRF output at
 * creation: a client-side `get()` with a locally generated random challenge for
 * the credential just created. The response is discarded after the PRF output
 * is read; the assert routes are never touched.
 */
async function prfFromFreshAssertion(
  registration: RegistrationResponseJSON,
  options: PublicKeyCredentialCreationOptionsJSON,
  prfSalt: Uint8Array,
): Promise<Uint8Array | null> {
  const publicKey = {
    challenge: crypto.getRandomValues(new Uint8Array(32)),
    rpId: options.rp.id,
    allowCredentials: [
      {
        type: 'public-key',
        id: base64URLStringToBuffer(registration.rawId),
        transports: registration.response.transports,
      },
    ],
    userVerification: 'required',
    extensions: { prf: { eval: { first: toArrayBuffer(prfSalt) } } },
  } as unknown as PublicKeyCredentialRequestOptions;
  try {
    const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
    return readPrfOutput(credential?.getClientExtensionResults());
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export interface CreatedPasskey {
  registration: RegistrationResponseJSON;
  wrap: KeyWrap;
}

/**
 * Create a passkey from server options, obtain its PRF output (at creation or
 * through the one-assertion fallback), and wrap the private key under it.
 * Throws `PasskeyError('prf-unavailable')` when no path returns a PRF output.
 */
export async function createPasskeyWithPrf(
  options: PublicKeyCredentialCreationOptionsJSON,
  prfSalt: Uint8Array,
  privateKey: Uint8Array,
): Promise<CreatedPasskey> {
  let registration: RegistrationResponseJSON;
  try {
    registration = await startRegistration({ optionsJSON: withPrfInput(options, prfSalt) });
  } catch (err) {
    throw classifyRegistrationError(err);
  }
  let prf = readPrfOutput(registration.clientExtensionResults);
  if (!prf) prf = await prfFromFreshAssertion(registration, options, prfSalt);
  if (!prf) throw new PasskeyError('prf-unavailable', PASSKEY_UNSUPPORTED_MESSAGE);
  try {
    const wrap = await wrapPrivateKeyWithPrf(prf, privateKey);
    return { registration: stripPrfResults(registration), wrap };
  } finally {
    prf.fill(0);
  }
}

/** Fetch `register/options` for a setup or identity-reset upload and create the passkey. */
export async function createIdentityPasskey(input: {
  prfSalt: Uint8Array;
  purpose: Extract<PasskeyRegistrationPurpose, 'setup' | 'identity_reset'>;
  privateKey: Uint8Array;
}): Promise<CreatedPasskey> {
  const options = await getPasskeyRegistrationOptions({
    prfSalt: toB64(input.prfSalt),
    purpose: input.purpose,
  });
  return createPasskeyWithPrf(options, input.prfSalt, input.privateKey);
}

/**
 * Add a passkey to an existing identity: `register/options` with purpose `add`
 * (the stored `prfSalt`, `excludeCredentials` = the existing ids) → `create()`
 * → PRF → wrap → `POST /passkeys/register`. The caller satisfies the settings
 * gate BEFORE this runs, because an assertion replaces the pending challenge.
 */
export async function addPasskey(input: {
  prfSalt: string;
  privateKey: Uint8Array;
  name: string;
}): Promise<PasskeyInfo> {
  const options = await getPasskeyRegistrationOptions({ prfSalt: input.prfSalt, purpose: 'add' });
  const created = await createPasskeyWithPrf(options, fromB64(input.prfSalt), input.privateKey);
  return registerPasskey({ name: input.name, registration: created.registration, wrap: created.wrap });
}

// ---------------------------------------------------------------------------
// Assertion
// ---------------------------------------------------------------------------

interface VerifiedAssertion {
  credentialId: string;
  wrap: KeyWrap;
  prf: Uint8Array | null;
}

async function runAssertion(): Promise<VerifiedAssertion> {
  const options: PublicKeyCredentialRequestOptionsJSON = await getPasskeyAssertionOptions();
  if (!options.allowCredentials || options.allowCredentials.length === 0) {
    assertionFailed = true;
    throw new PasskeyError('no-passkey', NO_PASSKEY_MESSAGE);
  }
  let assertion: AuthenticationResponseJSON;
  try {
    assertion = await startAuthentication({ optionsJSON: withPrfInput(options, new Uint8Array(0)) });
  } catch {
    assertionFailed = true;
    throw new PasskeyError('no-passkey', NO_PASSKEY_MESSAGE);
  }
  const prf = readPrfOutput(assertion.clientExtensionResults);
  let verified: { credentialId: string; wrap: KeyWrap };
  try {
    verified = await assertPasskey(stripPrfResults(assertion));
  } catch (err) {
    assertionFailed = true;
    prf?.fill(0);
    if ((err as { status?: number } | undefined)?.status === 401) {
      throw new PasskeyError('not-verified', PASSKEY_NOT_VERIFIED_MESSAGE);
    }
    throw err; // any other transport failure keeps the backend's message
  }
  lastAssertionAt = Date.now();
  return { ...verified, prf };
}

export interface PasskeyUnlock {
  privateKey: Uint8Array;
  credentialId: string;
}

/**
 * Unlock on a new or expired browser: `assert/options` → `get()` with PRF →
 * `POST /passkeys/assert` (returns only that credential's wrap) → unwrap.
 * `no-passkey` covers a cancelled or failed `get()` and an empty allow list;
 * `not-verified` a 401 from the assert route; `prf-unwrap-failed` a missing
 * PRF output or an unwrap failure.
 */
export async function unlockWithPasskey(): Promise<PasskeyUnlock> {
  const { credentialId, wrap, prf } = await runAssertion();
  if (!prf) throw new PasskeyError('prf-unwrap-failed', PASSKEY_CANNOT_UNLOCK_MESSAGE);
  try {
    return { privateKey: await unwrapPrivateKeyWithPrf(prf, wrap), credentialId };
  } catch {
    throw new PasskeyError('prf-unwrap-failed', PASSKEY_CANNOT_UNLOCK_MESSAGE);
  } finally {
    prf.fill(0);
  }
}

/** A settings-gate assertion: records `lastAssertedAt` server-side; the returned wrap is discarded. */
export async function assertForSettings(): Promise<void> {
  const { prf } = await runAssertion();
  prf?.fill(0);
}

// ---------------------------------------------------------------------------
// Listing / removal / naming
// ---------------------------------------------------------------------------

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  return listPasskeysRoute();
}

export async function removePasskey(credentialId: string): Promise<void> {
  return removePasskeyRoute(credentialId);
}

/** "<browser> on <OS>" from the user agent (1–64 characters), editable in the dialog. */
export function defaultPasskeyName(userAgent: string = navigator.userAgent): string {
  let browser = 'Browser';
  if (/Edg\//.test(userAgent)) browser = 'Edge';
  else if (/OPR\//.test(userAgent)) browser = 'Opera';
  else if (/Firefox\//.test(userAgent)) browser = 'Firefox';
  else if (/Chrome\//.test(userAgent)) browser = 'Chrome';
  else if (/Safari\//.test(userAgent)) browser = 'Safari';

  let os = 'this device';
  if (/Windows/.test(userAgent)) os = 'Windows';
  else if (/iPhone|iPad|iPod/.test(userAgent)) os = 'iOS';
  else if (/Mac OS X/.test(userAgent)) os = 'macOS';
  else if (/Android/.test(userAgent)) os = 'Android';
  else if (/CrOS/.test(userAgent)) os = 'ChromeOS';
  else if (/Linux/.test(userAgent)) os = 'Linux';

  return `${browser} on ${os}`.slice(0, 64);
}
